import { createContext, useCallback, useEffect, useRef } from "react";
import { InteractionEvent } from "@hulanbv/platformapp";
import { environment } from "../../constants/environment.constants";
import { authenticationService } from "../authentication/authentication.service";
import { webViewOpenedEvent } from "../common/injection.service";

type UseInteractionLoggerType = {
  startInteraction: (
    exerciseInviteId?: string,
    event?: InteractionEvent,
  ) => void;
};

// interactions shorter than this are considered noise and not logged
const minLoggableDurationMs = 1000;
// interactions are capped (not dropped) at this duration
const maxLoggableDurationMs = 3 * 60 * 60 * 1000;

const InteractionLoggerContext = createContext<UseInteractionLoggerType>({
  startInteraction: () => {
    /* default no-op */
  },
});

const useInteractionLogger = (): UseInteractionLoggerType => {
  const watchedInviteIdRef = useRef<string | null>(null);
  const watchedEventRef = useRef<InteractionEvent>(InteractionEvent.EXERCISE);
  const accumulatedMsRef = useRef<number>(0);
  // Date.now() at which the currently running segment started, null when
  // paused (page hidden or window blurred)
  const segmentStartedAtRef = useRef<number | null>(null);

  /** Folds the running segment into the accumulated duration and pauses. */
  const pauseSegment = useCallback(() => {
    if (segmentStartedAtRef.current !== null) {
      accumulatedMsRef.current += Date.now() - segmentStartedAtRef.current;
      segmentStartedAtRef.current = null;
    }
  }, []);

  /** Restarts a segment when an invite is being watched and none is running. */
  const resumeSegment = useCallback(() => {
    if (watchedInviteIdRef.current && segmentStartedAtRef.current === null) {
      segmentStartedAtRef.current = Date.now();
    }
  }, []);

  /**
   * Posts the accumulated duration for the watched invite. Uses a keepalive
   * fetch so the request survives page unload. When keepWatching is true the
   * invite stays watched so a later resumeSegment continues logging.
   */
  const flushInteraction = useCallback(
    (keepWatching: boolean) => {
      pauseSegment();

      const exerciseInviteId = watchedInviteIdRef.current;
      const event = watchedEventRef.current;
      const duration = Math.min(
        accumulatedMsRef.current,
        maxLoggableDurationMs,
      );
      accumulatedMsRef.current = 0;
      if (!keepWatching) {
        watchedInviteIdRef.current = null;
        watchedEventRef.current = InteractionEvent.EXERCISE;
      }

      const session = authenticationService.getSession();
      if (
        !exerciseInviteId ||
        !session?.userId ||
        duration < minLoggableDurationMs
      ) {
        return;
      }

      fetch([environment.apiUrl, "exercise-invite-interactions"].join("/"), {
        method: "POST",
        keepalive: true,
        headers: {
          "content-type": "application/json",
          authorization: session.token ?? "",
        },
        body: JSON.stringify({
          exerciseInviteId,
          userId: session.userId,
          timeSpentMs: duration,
          event,
        }),
      }).catch(() => {
        /* a failed log should never interrupt the user */
      });
    },
    [pauseSegment],
  );

  useEffect(() => {
    const handleFocus = () => resumeSegment();

    // Clicking into an iframe (e.g. embedded video) triggers a window "blur"
    // event. Treat that case as still focused so we keep tracking time.
    const handleBlur = () => {
      const active = document.activeElement;
      const isFocusingIframe = !!active && active.tagName === "IFRAME";
      if (!isFocusingIframe) {
        pauseSegment();
      }
    };

    // A hidden tab may never come back (tab close, app kill), so flush what
    // we have while keeping the invite watched in case the user returns.
    const handleVisibilityChange = () => {
      if (document.hidden) {
        flushInteraction(true);
      } else {
        resumeSegment();
      }
    };

    // Last chance before the page unloads; the keepalive fetch outlives it.
    const handlePageHide = () => flushInteraction(false);

    // The in-app browser (iOS SFSafariViewController) covers the page without
    // firing visibilitychange or blur, so the segment would keep counting
    // while the user is inside the webview. Flush and pause explicitly; the
    // invite stays watched so a focus event on return resumes logging.
    const handleWebViewOpened = () => flushInteraction(true);

    // Detect in-app navigation away from the watched invite. The invite id is
    // expected somewhere in the URL (path param or query string).
    const urlWatcher = window.setInterval(() => {
      const watchedId = watchedInviteIdRef.current;
      if (watchedId && !window.location.href.includes(watchedId)) {
        flushInteraction(false);
      }
    }, 1000);

    window.addEventListener("focus", handleFocus);
    window.addEventListener("blur", handleBlur);
    window.addEventListener("pagehide", handlePageHide);
    window.addEventListener(webViewOpenedEvent, handleWebViewOpened);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.clearInterval(urlWatcher);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("blur", handleBlur);
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener(webViewOpenedEvent, handleWebViewOpened);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [flushInteraction, pauseSegment, resumeSegment]);

  const startInteraction = useCallback(
    (exerciseInviteId?: string, event?: InteractionEvent) => {
      if (
        !exerciseInviteId ||
        watchedInviteIdRef.current === exerciseInviteId
      ) {
        // nothing to watch, or already watching this invite. A falsy id does
        // not stop a previous interaction; the url watcher handles that.
        return;
      }

      if (watchedInviteIdRef.current) {
        flushInteraction(false);
      }

      watchedInviteIdRef.current = exerciseInviteId;
      watchedEventRef.current = event ?? InteractionEvent.EXERCISE;
      accumulatedMsRef.current = 0;
      if (!document.hidden) {
        segmentStartedAtRef.current = Date.now();
      }
    },
    [flushInteraction],
  );

  return { startInteraction };
};

export { InteractionLoggerContext, useInteractionLogger };
